"use client";

import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithCredential,
  signInWithPopup,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  signOut as firebaseSignOut,
  User as FirebaseUser,
} from "firebase/auth";
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp, Unsubscribe } from "firebase/firestore";
import { auth, db } from "./firebase";
import type { User, SubjectStatus } from "@/types";
import { readFirstTouchCookie } from "./first-touch";

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  /** True iff the signed-in user has Firebase custom claim `admin: true`
   *  OR their email matches the operator allowlist. Mirrors the
   *  server-side check in src/lib/admin-guard.ts. Pure UX hint —
   *  any privileged server action still verifies the ID token's
   *  claims directly via requireAdmin(). */
  isAdmin: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  /** Returns `{ isNewUser }` so callers can choose between firing the
   *  "signup" event and the "login" event without re-querying Firestore. */
  signInWithGoogle: () => Promise<{ isNewUser: boolean }>;
  signInWithGoogleCredential: (idToken: string) => Promise<{ isNewUser: boolean }>;
  signOut: () => Promise<void>;
}

/** Client-side mirror of admin-guard.ts ADMIN_EMAIL_ALLOWLIST. The
 *  authoritative truth is the Firebase custom claim `admin: true`
 *  set server-side; this allowlist exists so a freshly-promoted
 *  admin sees admin UX before their token has a chance to refresh
 *  with the new claim. */
const CLIENT_ADMIN_EMAIL_ALLOWLIST: ReadonlyArray<string> = [
  "trinityprep103@gmail.com",
];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const EMPTY_SUBJECTS: SubjectStatus = { reading: false, math: false, language: false };
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/* ─── Migration + 7-day expiry check ─── */
async function migrateAndCheck(uid: string, data: Record<string, unknown>): Promise<void> {
  const updates: Record<string, unknown> = {};

  if (typeof data.freeSessionsRemaining !== "number") {
    const old = typeof data.sessionsRemaining === "number" ? (data.sessionsRemaining as number) : 0;
    updates.freeSessionsRemaining = 1;
    updates.paidSessionsRemaining = Math.max(0, old - 1);
  }
  if (!data.currentSessionSubjects) {
    updates.currentSessionSubjects = EMPTY_SUBJECTS;
    updates.currentSessionStartedAt = null;
  }
  if (!data.freeSessionSubjects) {
    updates.freeSessionSubjects = EMPTY_SUBJECTS;
  }

  const startedAt = data.currentSessionStartedAt as string | null;
  if (startedAt && Date.now() - new Date(startedAt).getTime() > SEVEN_DAYS_MS) {
    updates.currentSessionSubjects = EMPTY_SUBJECTS;
    updates.currentSessionStartedAt = null;
  }

  if (Object.keys(updates).length > 0) {
    await setDoc(doc(db, "users", uid), updates, { merge: true });
  }
}

function snapshotToUser(uid: string, data: Record<string, unknown>): User {
  return {
    uid,
    email: (data.email as string) ?? "",
    displayName: (data.displayName as string) ?? "",
    role: (data.role as User["role"]) ?? "parent",
    plan: (data.plan as User["plan"]) ?? "free",
    sessionsRemaining: (data.sessionsRemaining as number) ?? 0,
    freeSessionsRemaining: (data.freeSessionsRemaining as number) ?? 0,
    paidSessionsRemaining: (data.paidSessionsRemaining as number) ?? 0,
    currentSessionSubjects: (data.currentSessionSubjects as SubjectStatus) ?? EMPTY_SUBJECTS,
    currentSessionStartedAt: (data.currentSessionStartedAt as string | null) ?? null,
    freeSessionSubjects: (data.freeSessionSubjects as SubjectStatus) ?? EMPTY_SUBJECTS,
    subjects: (data.subjects as string[]) ?? ["reading"],
    createdAt: data.createdAt as Date,
    // hasSeenWelcome: read as-is. Absent → undefined → no modal (only
    // strict `=== false` shows it). Set to false at signup so the
    // first dashboard visit triggers it once.
    hasSeenWelcome: data.hasSeenWelcome as boolean | undefined,
    unlimitedUntil: typeof data.unlimitedUntil === "number" ? data.unlimitedUntil : null,
    unlimitedDayKey: typeof data.unlimitedDayKey === "string" ? data.unlimitedDayKey : null,
    unlimitedDayCount: typeof data.unlimitedDayCount === "number" ? data.unlimitedDayCount : 0,
    grade: typeof data.grade === "number" && Number.isFinite(data.grade) ? data.grade : null,
  };
}

function newUserDefaults(uid: string, email: string, displayName: string): User {
  return {
    uid, email, displayName,
    role: "parent",
    plan: "free",
    sessionsRemaining: 1,
    freeSessionsRemaining: 1,
    paidSessionsRemaining: 0,
    currentSessionSubjects: EMPTY_SUBJECTS,
    currentSessionStartedAt: null,
    freeSessionSubjects: EMPTY_SUBJECTS,
    subjects: ["reading"],
    createdAt: new Date(),
    hasSeenWelcome: false,
  };
}

function newUserFirestore(user: User) {
  // 첫 유입 경로 (UTM·참조 주소 · src/lib/first-touch.ts · 2026-10-13) — 쿠키가 있을 때만
  const firstTouch = readFirstTouchCookie();
  return {
    ...user,
    sessionsLeft: 1,
    totalSessions: 0,
    createdAt: serverTimestamp(),
    ...(firstTouch ? { firstTouch } : {}),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const userSnapshotUnsub = useRef<Unsubscribe | null>(null);
  const migrated = useRef(false);

  const detachUserListener = () => {
    if (userSnapshotUnsub.current) {
      userSnapshotUnsub.current();
      userSnapshotUnsub.current = null;
    }
  };

  useEffect(() => {
    let unsubAuth: (() => void) | null = null;
    // Re-seeds the admin-guard session cookie every 50 minutes so it never
    // outlives the underlying Firebase ID token (1h TTL). Previously only
    // /admin/page.tsx ran this loop, which meant deep-linking straight to
    // /admin/analytics after >1h would hit an expired cookie and redirect.
    let cookieRefreshTimer: ReturnType<typeof setInterval> | null = null;

    const setupAuthListener = () => {
      unsubAuth = onAuthStateChanged(auth, async (fbUser) => {
        setFirebaseUser(fbUser);
        detachUserListener();
        migrated.current = false;
        if (cookieRefreshTimer) {
          clearInterval(cookieRefreshTimer);
          cookieRefreshTimer = null;
        }

        // Admin detection: optimistic email-allowlist check first
        // (synchronous, runs immediately on auth state change), then
        // upgraded by the canonical custom-claim read once the
        // forceRefreshed ID token comes back below.
        if (fbUser) {
          const email = (fbUser.email ?? "").toLowerCase();
          setIsAdmin(email !== "" && CLIENT_ADMIN_EMAIL_ALLOWLIST.includes(email));
        } else {
          setIsAdmin(false);
        }

        if (fbUser) {
          const userRef = doc(db, "users", fbUser.uid);
          const snap = await getDoc(userRef);
          if (!snap.exists()) {
            const newUser = newUserDefaults(
              fbUser.uid,
              fbUser.email ?? "",
              fbUser.displayName ?? fbUser.email?.split("@")[0] ?? "",
            );
            await setDoc(userRef, newUserFirestore(newUser));
          }

          // Canonical admin check MUST be awaited before onSnapshot below
          // flips `loading` false — otherwise a freshly-granted admin
          // (claim=true, but email not in the sync CLIENT_ADMIN_EMAIL_ALLOWLIST
          // applied earlier) briefly appears as (loading=false, isAdmin=false)
          // and any consumer's `if (!loading && !isAdmin) redirect` fires
          // before the claim upgrade lands. Previously this ran inside a
          // fire-and-forget IIFE and raced with onSnapshot — see fix.
          //
          // We still force-refresh the ID token so custom claims granted
          // server-side after the browser's original token was issued are
          // picked up immediately (no 1h wait). If the refresh fails because
          // the refresh token was revoked (admin demotion, password reset,
          // or explicit revokeRefreshTokens), sign the user out so they re-
          // auth cleanly — otherwise they'd be stuck in "logged in client-
          // side, rejected server-side" limbo where every cookie sync 401s.
          let idToken: string | null = null;
          try {
            idToken = await fbUser.getIdToken(/* forceRefresh */ true);
            const result = await fbUser.getIdTokenResult(false);
            const emailLower = (fbUser.email ?? "").toLowerCase();
            const claimAdmin = result.claims.admin === true;
            const emailAdmin =
              emailLower !== "" && CLIENT_ADMIN_EMAIL_ALLOWLIST.includes(emailLower);
            setIsAdmin(claimAdmin || emailAdmin);
          } catch (err) {
            const code = (err as { code?: string })?.code ?? "";
            if (
              code === "auth/user-token-expired" ||
              code === "auth/user-disabled" ||
              code === "auth/id-token-revoked" ||
              code === "auth/invalid-user-token"
            ) {
              try { await firebaseSignOut(auth); } catch { /* best-effort */ }
              return; // onAuthStateChanged will re-fire with fbUser=null
            }
            // Any other error (network etc): retain the sync email-allowlist
            // value already applied above and proceed — we MUST reach
            // setLoading(false) so the UI unblocks.
          }

          // Cookie sync + referral provisioning do NOT gate isAdmin or
          // loading. Kept fire-and-forget; session cookie posted first so
          // the admin-guard server component sees it as soon as possible.
          if (idToken) {
            const tokenForBg = idToken;
            void (async () => {
              try {
                await fetch("/api/auth/session", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ idToken: tokenForBg }),
                });
                await fetch("/api/referral/code", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${tokenForBg}`,
                  },
                  body: JSON.stringify({
                    ownerName:
                      fbUser.displayName || fbUser.email?.split("@")[0] || "",
                  }),
                });
              } catch {
                /* fire-and-forget — cookieRefreshTimer will retry every 50min */
              }
            })();
          }

          // Keep the admin-guard cookie alive while the user remains signed
          // in. Uses cached token (no forceRefresh) so this is cheap; the
          // initial sync above already forced a refresh for fresh claims.
          cookieRefreshTimer = setInterval(async () => {
            try {
              const idToken = await fbUser.getIdToken(false);
              await fetch("/api/auth/session", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ idToken }),
              });
            } catch {
              /* fire-and-forget — onAuthStateChanged will catch a hard fail */
            }
          }, 50 * 60 * 1000);

          userSnapshotUnsub.current = onSnapshot(userRef, async (docSnap) => {
            if (!docSnap.exists()) return;
            const data = docSnap.data();
            if (!migrated.current) {
              migrated.current = true;
              await migrateAndCheck(fbUser.uid, data);
            }
            setUser(snapshotToUser(fbUser.uid, data));
            setLoading(false);
          });
        } else {
          setUser(null);
          setLoading(false);
        }
      });
    };

    setupAuthListener();

    return () => {
      if (unsubAuth) unsubAuth();
      if (cookieRefreshTimer) clearInterval(cookieRefreshTimer);
      detachUserListener();
    };
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const newUser = newUserDefaults(cred.user.uid, email, displayName);
    await setDoc(doc(db, "users", cred.user.uid), newUserFirestore(newUser));
    try {
      await fetch("/api/messages/welcome", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid: cred.user.uid }),
      });
    } catch (e) {
      console.error("welcome msg failed", e);
    }
  };

  /* Firebase signInWithPopup — handles OAuth via Firebase authDomain.
   * Returns Firebase's authoritative isNewUser flag (from
   * `getAdditionalUserInfo`) so the caller knows whether to fire the
   * "signup" event or the "login" event. The Firestore users-doc
   * create is gated on `!userDoc.exists()` and never touches createdAt
   * on subsequent calls, so re-logins keep their original timestamp. */
  const signInWithGoogle = async (): Promise<{ isNewUser: boolean }> => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    const result = await signInWithPopup(auth, provider);
    const isNewUser = getAdditionalUserInfo(result)?.isNewUser ?? false;
    const userDoc = await getDoc(doc(db, "users", result.user.uid));
    if (!userDoc.exists()) {
      const newUser = newUserDefaults(result.user.uid, result.user.email ?? "", result.user.displayName ?? "");
      await setDoc(doc(db, "users", result.user.uid), newUserFirestore(newUser));
      try {
        await fetch("/api/messages/welcome", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uid: result.user.uid }),
        });
      } catch (e) {
        console.error("welcome msg failed", e);
      }
    }
    return { isNewUser };
  };

  /* GIS gives us a Google ID token — exchange it for Firebase credential (legacy) */
  const signInWithGoogleCredential = async (idToken: string): Promise<{ isNewUser: boolean }> => {
    const credential = GoogleAuthProvider.credential(idToken);
    const result = await signInWithCredential(auth, credential);
    const isNewUser = getAdditionalUserInfo(result)?.isNewUser ?? false;
    const userDoc = await getDoc(doc(db, "users", result.user.uid));
    if (!userDoc.exists()) {
      const newUser = newUserDefaults(result.user.uid, result.user.email ?? "", result.user.displayName ?? "");
      await setDoc(doc(db, "users", result.user.uid), newUserFirestore(newUser));
      try {
        await fetch("/api/messages/welcome", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uid: result.user.uid }),
        });
      } catch (e) {
        console.error("welcome msg failed", e);
      }
    }
    return { isNewUser };
  };

  const signOut = async () => {
    detachUserListener();
    await firebaseSignOut(auth);
    // Clear the server-side admin session cookie so a stale token can't be
    // replayed against the admin layout after signout.
    try {
      await fetch("/api/auth/session", { method: "DELETE" });
    } catch {
      /* best-effort */
    }
    setUser(null);
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ user, firebaseUser, loading, isAdmin, signInWithEmail, signUp, signInWithGoogle, signInWithGoogleCredential, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
